class ParallaxBanner {
  constructor() {
    this.banners = document.querySelectorAll('.banner--parallax');
    this.init();
  }

  init() {
    if (!this.banners.length) return;
    
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Check if mobile and disable parallax
    this.checkMobile();
    
    // Set up resize listener to handle responsive changes
    window.addEventListener('resize', () => {
      this.checkMobile();
    });
  }
  
  checkMobile() {
    // Enable parallax on all devices now
    if (!this.parallaxEnabled) {
      this.setupParallax();
      this.bindEvents();
    }
  }
  
  disableParallax() {
    this.parallaxEnabled = false;
    this.banners.forEach(banner => {
      banner.classList.remove('parallax-active');
      const media = banner.querySelector('.banner__media');
      if (media) {
        media.style.removeProperty('--parallax-offset');
      }
    });
  }

  setupParallax() {
    this.parallaxEnabled = true;
    const isMobile = window.innerWidth <= 749;
    
    this.banners.forEach(banner => {
      banner.classList.add('parallax-active');
      const media = banner.querySelector('.banner__media');
      if (media) {
        media.classList.add('parallax-container');
        // Force initial offset to 0 on mobile
        if (isMobile) {
          media.style.setProperty('--parallax-offset', '0px');
        }
      }
    });

    // Initial position - skip on mobile to prevent jump
    if (!isMobile) {
      this.updateParallax();
    }
  }

  bindEvents() {
    if (this.scrollHandler) return; // Already bound
    
    let ticking = false;
    
    this.scrollHandler = () => {
      if (!ticking && this.parallaxEnabled) {
        window.requestAnimationFrame(() => {
          this.updateParallax();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Throttled scroll event
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  updateParallax() {
    const scrolled = window.pageYOffset;
    const windowHeight = window.innerHeight;

    this.banners.forEach(banner => {
      const media = banner.querySelector('.banner__media');
      if (!media) return;

      const bannerTop = banner.offsetTop;
      const bannerHeight = banner.offsetHeight;
      const bannerBottom = bannerTop + bannerHeight;

      // Check if banner is in viewport
      if (scrolled + windowHeight > bannerTop && scrolled < bannerBottom) {
        // Get intensity from data attribute or use default
        const intensity = parseFloat(banner.dataset.parallaxIntensity || 30) / 100;
        
        // Responsive parallax settings
        const isMobile = window.innerWidth <= 749;
        const speed = isMobile ? 0.25 * intensity : 0.3; // Mobile scales by intensity, desktop fixed
        const maxOffset = bannerHeight * (isMobile ? 0.3 * intensity : 0.18); // Desktop at 18% for longer effect (was 10%)
        
        // Calculate raw offset
        let yPos;
        
        // Mobile: Different calculation to prevent any jump
        if (isMobile) {
          // Ultra smooth parallax for mobile - no jumps
          // Only move image down as user scrolls, never up
          yPos = scrolled * speed * (0.6 + intensity * 0.4); // Scale effect by intensity
          
          // Ensure we start at exactly 0 for very small scroll values
          if (scrolled < 2) {
            yPos = 0;
          }
        } else {
          // Desktop: Keep original calculation
          yPos = -(scrolled - bannerTop) * speed;
        }
        
        // Clamp the offset to prevent overflow
        yPos = Math.max(-maxOffset, Math.min(maxOffset, yPos));
        
        // Apply transform using CSS custom property for better performance
        media.style.setProperty('--parallax-offset', `${yPos}px`);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ParallaxBanner();
  });
} else {
  new ParallaxBanner();
}

// Reinitialize on Shopify theme editor changes
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ParallaxBanner();
  });
}